import { useState, useEffect, useCallback, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  formUrls,
  modalTitles,
  modalImages,
  defaultModalImage,
  infoOnlyTypes,
  whatsappHref,
} from "@/mocks/modalConfig";
import { modalContent } from "@/mocks/modalContent";
import { isSubmissionRateLimited, registerSubmission } from "@/utils/formGuard";

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType: string | null;
  onOpenQuote?: (service?: string) => void;
}

function isFormType(type: string): boolean {
  return !!formUrls[type];
}

async function submitForm(url: string, form: HTMLFormElement) {
  const formData = new FormData(form);

  const honeypot = formData.get("company_alt")?.toString().trim() || "";
  if (honeypot) {
    return { success: true, fake: true };
  }

  if (isSubmissionRateLimited()) {
    return { success: false, message: "Demasiados intentos seguidos. Espera un minuto e intenta de nuevo." };
  }

  const payload = new URLSearchParams();
  formData.forEach((value, key) => {
    if (key !== "company_alt") {
      payload.append(key, value.toString());
    }
  });

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: payload.toString(),
  });

  const responseText = await response.text();
  let parsed: Record<string, unknown> | null = null;
  try {
    parsed = JSON.parse(responseText);
  } catch {
    parsed = null;
  }

  const serverMsg =
    (parsed?.meta as Record<string, string>)?.message ||
    (parsed?.meta as Record<string, string>)?.detail ||
    (parsed?.message as string) ||
    responseText;

  const isSpam =
    typeof serverMsg === "string" &&
    (serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam"));

  if (!response.ok || isSpam || (parsed && (parsed.code as string) !== "OK")) {
    return { success: false, message: serverMsg || "Error al enviar. Por favor intenta de nuevo." };
  }

  registerSubmission();
  return { success: true };
}

const infoOnlySet = new Set(infoOnlyTypes);

export default function InfoModal({ isOpen, onClose, modalType, onOpenQuote }: InfoModalProps) {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormStatus("idle");
      setFormError("");
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, modalType]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setVisible(false);
        setTimeout(onClose, 200);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleFormSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!modalType) return;
      setFormStatus("submitting");
      setFormError("");

      const url = formUrls[modalType];
      if (!url) {
        setFormStatus("error");
        setFormError("Formulario no configurado");
        return;
      }

      try {
        const result = await submitForm(url, e.currentTarget);
        if (result.success) {
          setFormStatus("success");
          e.currentTarget.reset();
        } else {
          setFormStatus("error");
          setFormError(result.message || "Error al enviar");
        }
      } catch {
        setFormStatus("error");
        setFormError("Error de conexión. Intenta más tarde.");
      }
    },
    [modalType]
  );

  if (!isOpen || !modalType) return null;

  const title = modalTitles[modalType] || "Información";
  const headerImage = modalImages[modalType] || defaultModalImage;
  const isForm = isFormType(modalType);
  const content = modalContent[modalType];
  const showOfferCta = !isForm && !infoOnlySet.has(modalType);

  return (
    <div
      className={`fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-4 transition-all duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm transition-opacity duration-200"
        onClick={handleClose}
        role="presentation"
      />

      {/* Card */}
      <div
        className={`relative w-full max-w-[540px] max-h-[92vh] flex flex-col overflow-hidden rounded-3xl bg-[#0E0F14] border border-white/10 transition-all duration-300 ${
          visible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
      >
        {/* Header image */}
        <div className="relative h-36 sm:h-44 flex-shrink-0">
          <img
            src={headerImage}
            alt={title}
            width={800}
            height={320}
            className="w-full h-full object-cover object-center"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0F14] via-[#0E0F14]/35 to-black/25" />

          {content?.badge && (
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F97316] text-white text-[10px] font-label font-semibold uppercase tracking-[0.12em]">
              {content.badge}
            </span>
          )}

          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/45 backdrop-blur-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition-all duration-200 cursor-pointer"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 sm:px-7 py-5">
          {isForm ? (
            <FormContent
              type={modalType}
              title={title}
              formStatus={formStatus}
              formError={formError}
              onSubmit={handleFormSubmit}
              onReset={() => setFormStatus("idle")}
            />
          ) : (
            <InfoBody type={modalType} title={title} />
          )}
        </div>

        {/* Footer CTAs */}
        {showOfferCta && (
          <div className="flex-shrink-0 px-5 sm:px-7 py-4 border-t border-white/10 bg-[#111319]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onOpenQuote?.(title)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#3E6FE6] text-white font-label font-semibold text-sm hover:bg-[#335fc9] transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line" />
                Cotizar este servicio
              </button>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#F97316] text-white font-label font-semibold text-sm hover:bg-[#e4630a] transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line" />
                Consultar por WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoBody({ type, title }: { type: string; title: string }) {
  const content = modalContent[type];

  if (!content) {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 mb-4">
          <i className="ri-information-line text-xl text-[#5B9BFF]" />
        </div>
        <p className="text-sm text-white/60 mb-2">Contenido detallado próximamente.</p>
        <p className="text-xs text-white/40 max-w-xs mx-auto">
          Estamos actualizando esta sección. Para más información, contacta a hola@openlabsecure.com
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {content.category && (
        <p className="text-[10px] font-label font-semibold text-[#5B9BFF] uppercase tracking-[0.25em]">
          {content.category}
        </p>
      )}

      <h3 className="font-heading font-bold text-xl sm:text-2xl text-white leading-tight">{title}</h3>

      <p className="text-sm text-white/60 leading-relaxed">{content.intro}</p>

      {content.items.length > 0 && (
        <div>
          <p className="text-[10px] font-label font-semibold text-white/45 uppercase tracking-[0.2em] mb-3">
            ¿Qué incluye?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
            {content.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <i className="ri-checkbox-fill text-[#5B9BFF] text-base mt-[1px] flex-shrink-0" />
                <span className="text-xs text-white/70 leading-snug">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {content.stats && content.stats.length > 0 && (
        <div className="grid grid-cols-3 gap-2 py-4 border-y border-white/10">
          {content.stats.map((stat, i) => (
            <div key={i} className="text-center px-1">
              <p className="font-heading font-bold text-lg sm:text-xl text-[#5B9BFF] leading-none break-words">
                {stat.value}
              </p>
              <p className="text-[10px] font-label text-white/40 uppercase tracking-wider mt-1.5 leading-tight">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      )}

      {content.useCases && content.useCases.length > 0 && (
        <div>
          <p className="text-[10px] font-label font-semibold text-white/45 uppercase tracking-[0.2em] mb-3">
            Casos de uso
          </p>
          <div className="flex flex-wrap gap-2">
            {content.useCases.map((useCase, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70"
              >
                <i className="ri-briefcase-4-line text-[#F97316] text-sm" />
                {useCase}
              </span>
            ))}
          </div>
        </div>
      )}

      {type.startsWith("datacenter-") && (
        <Link
          to="/datacenter"
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer group"
        >
          <span className="text-xs font-label font-semibold text-[#5B9BFF]">Ver página completa</span>
          <i className="ri-arrow-right-line text-sm text-[#5B9BFF] group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      )}
    </div>
  );
}

function FormContent({
  type,
  title,
  formStatus,
  formError,
  onSubmit,
  onReset,
}: {
  type: string;
  title: string;
  formStatus: string;
  formError: string;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onReset: () => void;
}) {
  if (formStatus === "success") {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-400/25 mb-5">
          <i className="ri-check-line text-2xl text-emerald-400" />
        </div>
        <h4 className="font-heading font-semibold text-lg text-white mb-2">¡Enviado correctamente!</h4>
        <p className="text-sm text-white/55 mb-5 max-w-xs mx-auto">
          Hemos recibido tu solicitud. Te contactaremos a la brevedad.
        </p>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center px-5 py-2.5 rounded-full bg-white/5 border border-white/15 text-white/80 font-label font-semibold text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-1.5" />
          Enviar otro
        </button>
      </div>
    );
  }

  const inputBase =
    "w-full px-4 py-3 rounded-xl bg-white/5 border border-white/12 text-white text-sm placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#5B9BFF]/25 focus:border-[#5B9BFF]/50 transition-all";
  const labelBase =
    "block text-[11px] font-label font-semibold text-white/55 mb-1.5 uppercase tracking-wider";

  return (
    <form onSubmit={onSubmit} data-readdy-form className="space-y-4">
      <p className="text-[10px] font-label font-semibold text-[#5B9BFF] uppercase tracking-[0.25em]">
        Formulario
      </p>
      <h3 className="font-heading font-bold text-xl text-white leading-tight -mt-3">{title}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fm-name" className={labelBase}>
            Nombre completo <span className="text-rose-400">*</span>
          </label>
          <input type="text" id="fm-name" name="name" required className={inputBase} placeholder="Tu nombre" />
        </div>
        <div>
          <label htmlFor="fm-email" className={labelBase}>
            Email <span className="text-rose-400">*</span>
          </label>
          <input type="email" id="fm-email" name="email" required className={inputBase} placeholder="tu@email.com" />
        </div>
      </div>

      {type === "libro-reclamaciones" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fm-doc-type" className={labelBase}>
                Tipo de documento
              </label>
              <select id="fm-doc-type" name="tipo_documento" className={inputBase}>
                <option value="dni">DNI</option>
                <option value="ce">CE</option>
                <option value="ruc">RUC</option>
                <option value="pasaporte">Pasaporte</option>
              </select>
            </div>
            <div>
              <label htmlFor="fm-doc-num" className={labelBase}>
                Número de documento
              </label>
              <input type="text" id="fm-doc-num" name="numero_documento" className={inputBase} placeholder="Número" />
            </div>
          </div>
          <div>
            <label htmlFor="fm-recl-type" className={labelBase}>
              Tipo de solicitud
            </label>
            <select id="fm-recl-type" name="tipo_reclamacion" className={inputBase}>
              <option value="reclamo">Reclamo</option>
              <option value="queja">Queja</option>
              <option value="consulta">Consulta</option>
            </select>
          </div>
        </>
      )}

      {type === "derechos-arco" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fm-doc-type-arco" className={labelBase}>
                Tipo de documento
              </label>
              <select id="fm-doc-type-arco" name="tipo_documento" className={inputBase}>
                <option value="dni">DNI</option>
                <option value="ce">CE</option>
                <option value="pasaporte">Pasaporte</option>
              </select>
            </div>
            <div>
              <label htmlFor="fm-doc-num-arco" className={labelBase}>
                Número de documento
              </label>
              <input type="text" id="fm-doc-num-arco" name="numero_documento" className={inputBase} placeholder="Número" />
            </div>
          </div>
          <div>
            <label htmlFor="fm-arco-type" className={labelBase}>
              Derecho a ejercer
            </label>
            <select id="fm-arco-type" name="derecho_arco" className={inputBase}>
              <option value="acceso">Acceso</option>
              <option value="rectificacion">Rectificación</option>
              <option value="cancelacion">Cancelación</option>
              <option value="oposicion">Oposición</option>
            </select>
          </div>
        </>
      )}

      {type === "formulario-reclamo" && (
        <div>
          <label htmlFor="fm-phone" className={labelBase}>
            Teléfono
          </label>
          <input type="tel" id="fm-phone" name="telefono" className={inputBase} placeholder="+51 999 999 999" />
        </div>
      )}

      {type === "formulario-apelacion" && (
        <div>
          <label htmlFor="fm-ref" className={labelBase}>
            Referencia de caso
          </label>
          <input type="text" id="fm-ref" name="referencia_caso" className={inputBase} placeholder="Número de caso anterior" />
        </div>
      )}

      {type === "formulario-queja" && (
        <div>
          <label htmlFor="fm-depto" className={labelBase}>
            Departamento / Área
          </label>
          <input type="text" id="fm-depto" name="departamento" className={inputBase} placeholder="Área involucrada" />
        </div>
      )}

      <div>
        <label htmlFor="fm-asunto" className={labelBase}>
          Asunto
        </label>
        <input type="text" id="fm-asunto" name="asunto" className={inputBase} placeholder="Asunto de tu solicitud" />
      </div>

      <div>
        <label htmlFor="fm-detalle" className={labelBase}>
          Detalle <span className="text-rose-400">*</span>
        </label>
        <textarea
          id="fm-detalle"
          name="detalle"
          required
          rows={3}
          maxLength={500}
          className={`${inputBase} resize-none`}
          placeholder="Describe tu solicitud en detalle..."
        />
        <p className="text-[10px] text-white/35 mt-1 text-right">Máximo 500 caracteres</p>
      </div>

      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

      {formError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-400/25 text-rose-300 text-xs">
          <i className="ri-error-warning-line flex-shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={formStatus === "submitting"}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#3E6FE6] text-white font-label font-semibold text-sm hover:bg-[#335fc9] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
      >
        {formStatus === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            Enviar solicitud
            <i className="ri-send-plane-line" />
          </>
        )}
      </button>

      <p className="text-[10px] text-white/35 text-center leading-relaxed">
        Al enviar, aceptas nuestra{" "}
        <Link to="/politica-privacidad" className="text-[#5B9BFF] hover:underline cursor-pointer">
          Política de Privacidad
        </Link>{" "}
        y los{" "}
        <Link to="/terminos" className="text-[#5B9BFF] hover:underline cursor-pointer">
          Términos y Condiciones
        </Link>
        .
      </p>
    </form>
  );
}